import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Database,
  HardDrive,
  Wifi,
  WifiOff,
  Server,
  Mail,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  RefreshCw,
  Filter,
  Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Card } from '../../../core/shared/components/Card.js';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';

// ─── Types ──────────────────────────────────────────────────────

interface HealthCheck {
  status: string;
  latency?: number;
  details?: string;
}

interface HealthData {
  status: string;
  timestamp: string;
  uptime: number;
  checks: Record<string, HealthCheck>;
}

interface StatsData {
  gebruikers: { totaal: number };
  emails: { vandaag: number; week: number; maand: number };
  databus: { pending: number; sent: number; processed: number; failed: number };
  api: { gemiddeldeResponseTime: number; requestsLaatste24u: number };
  errors: { laatste24u: number };
}

interface DatabusEventRow {
  id: string;
  type: string;
  direction: string;
  status: string;
  attempts: number;
  error: string | null;
  createdAt: string;
}

interface ErrorLogRow {
  id: string;
  level: string;
  source: string;
  message: string;
  createdAt: string;
}

// ─── Helpers ────────────────────────────────────────────────────

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDatum(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function statusIcon(status: string) {
  switch (status) {
    case 'ok':
    case 'configured':
    case 'healthy':
      return <CheckCircle size={16} className="text-green-500" />;
    case 'error':
    case 'unreachable':
      return <XCircle size={16} className="text-red-500" />;
    default:
      return <AlertTriangle size={16} className="text-amber-500" />;
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'SENT':
    case 'PROCESSED':
      return 'bg-green-100 text-green-700';
    case 'PENDING':
      return 'bg-amber-100 text-amber-700';
    case 'FAILED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function levelColor(level: string): string {
  switch (level) {
    case 'ERROR':
      return 'bg-red-100 text-red-700';
    case 'WARN':
      return 'bg-amber-100 text-amber-700';
    case 'INFO':
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// ─── Component ──────────────────────────────────────────────────

export function SystemDashboard() {
  const navigate = useNavigate();
  const [eventFilter, setEventFilter] = useState<string>('');
  const [eventDirection, setEventDirection] = useState<string>('');

  const { data: health, isLoading: healthLoading, refetch: refetchHealth } = useQuery({
    queryKey: ['system-health'],
    queryFn: () => apiFetch<HealthData>('/api/health/detailed'),
    refetchInterval: 30000,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['system-stats'],
    queryFn: () => apiFetch<StatsData>('/api/system/stats'),
    refetchInterval: 60000,
  });

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['system-events', eventFilter, eventDirection],
    queryFn: () => {
      const params = new URLSearchParams();
      if (eventFilter) params.set('status', eventFilter);
      if (eventDirection) params.set('direction', eventDirection);
      params.set('limit', '20');
      return apiFetch<{ events: DatabusEventRow[]; total: number }>(`/api/system/events?${params}`);
    },
  });

  const { data: errorsData, isLoading: errorsLoading } = useQuery({
    queryKey: ['system-errors'],
    queryFn: () => apiFetch<{ logs: ErrorLogRow[]; total: number }>('/api/system/errors?limit=20'),
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-lg p-2 hover:bg-gray-100">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Systeem Dashboard</h1>
            <p className="text-sm text-gray-500">Health, monitoring & databus events</p>
          </div>
        </div>
        <button
          onClick={() => { refetchHealth(); }}
          className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw size={14} />
          Vernieuw
        </button>
      </div>

      {/* Health Status Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {healthLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="!p-4">
              <div className="h-16 animate-pulse rounded bg-gray-100" />
            </Card>
          ))
        ) : health ? (
          <>
            {/* Overall status */}
            <Card className="!p-4">
              <div className="flex items-center gap-3">
                <div className={clsx('flex h-10 w-10 items-center justify-center rounded-lg', health.status === 'healthy' ? 'bg-green-100' : 'bg-amber-100')}>
                  <Server size={20} className={health.status === 'healthy' ? 'text-green-600' : 'text-amber-600'} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="text-lg font-bold text-gray-900 capitalize">{health.status}</p>
                  <p className="text-xs text-gray-400">Uptime: {formatUptime(health.uptime)}</p>
                </div>
              </div>
            </Card>

            {/* Individual service checks */}
            {Object.entries(health.checks).map(([service, check]) => {
              const icons: Record<string, React.ReactNode> = {
                database: <Database size={20} className="text-blue-600" />,
                r2: <HardDrive size={20} className="text-purple-600" />,
                wms: check.status === 'ok' ? <Wifi size={20} className="text-green-600" /> : <WifiOff size={20} className="text-gray-400" />,
              };
              const bgColors: Record<string, string> = {
                database: 'bg-blue-100',
                r2: 'bg-purple-100',
                wms: check.status === 'ok' ? 'bg-green-100' : 'bg-gray-100',
              };
              return (
                <Card key={service} className="!p-4">
                  <div className="flex items-center gap-3">
                    <div className={clsx('flex h-10 w-10 items-center justify-center rounded-lg', bgColors[service] || 'bg-gray-100')}>
                      {icons[service] || <Activity size={20} className="text-gray-600" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 capitalize">{service}</p>
                      <div className="flex items-center gap-1.5">
                        {statusIcon(check.status)}
                        <span className="text-sm font-medium text-gray-900 capitalize">{check.status}</span>
                      </div>
                      {check.latency !== undefined && (
                        <p className="text-xs text-gray-400">{check.latency}ms</p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </>
        ) : null}
      </div>

      {/* Stats Overview */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Card className="!p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100">
                <Users size={18} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.gebruikers.totaal}</p>
                <p className="text-xs text-gray-500">Gebruikers</p>
              </div>
            </div>
          </Card>

          <Card className="!p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-100">
                <Mail size={18} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.emails.vandaag}</p>
                <p className="text-xs text-gray-500">Emails vandaag</p>
              </div>
            </div>
          </Card>

          <Card className="!p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100">
                <Zap size={18} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.api.requestsLaatste24u}</p>
                <p className="text-xs text-gray-500">API calls (24u)</p>
              </div>
            </div>
          </Card>

          <Card className="!p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100">
                <Clock size={18} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.api.gemiddeldeResponseTime}ms</p>
                <p className="text-xs text-gray-500">Gem. response</p>
              </div>
            </div>
          </Card>

          <Card className="!p-4">
            <div className="flex items-center gap-3">
              <div className={clsx('flex h-9 w-9 items-center justify-center rounded-lg', stats.errors.laatste24u > 0 ? 'bg-red-100' : 'bg-green-100')}>
                <AlertTriangle size={18} className={stats.errors.laatste24u > 0 ? 'text-red-600' : 'text-green-600'} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.errors.laatste24u}</p>
                <p className="text-xs text-gray-500">Errors (24u)</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Databus Stats Summary */}
      {!statsLoading && stats && (
        <Card>
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Databus Overzicht</h3>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(stats.databus).map(([key, value]) => (
              <div key={key} className="rounded-lg bg-gray-50 p-3 text-center">
                <p className="text-xl font-bold text-gray-900">{value}</p>
                <p className="text-xs capitalize text-gray-500">{key}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Two columns: Events + Errors */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Databus Event Log */}
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Recente Databus Events</h3>
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-400" />
              <select
                value={eventDirection}
                onChange={(e) => setEventDirection(e.target.value)}
                className="rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gold"
              >
                <option value="">Alle richtingen</option>
                <option value="OUTBOUND">Outbound</option>
                <option value="INBOUND">Inbound</option>
              </select>
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                className="rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gold"
              >
                <option value="">Alle statussen</option>
                <option value="PENDING">Pending</option>
                <option value="SENT">Sent</option>
                <option value="PROCESSED">Processed</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>

          <div className="max-h-80 space-y-1.5 overflow-y-auto">
            {eventsLoading ? (
              <div className="animate-pulse space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-10 rounded bg-gray-100" />
                ))}
              </div>
            ) : (eventsData?.events ?? []).length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">Geen events gevonden</p>
            ) : (
              (eventsData?.events ?? []).map((evt) => (
                <div key={evt.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', statusColor(evt.status))}>
                        {evt.status}
                      </span>
                      <span className="truncate text-sm font-medium text-gray-800">{evt.type}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-400">
                      <span>{evt.direction}</span>
                      <span>·</span>
                      <span>{formatDatum(evt.createdAt)}</span>
                      {evt.attempts > 1 && <span>· {evt.attempts} pogingen</span>}
                    </div>
                  </div>
                  {evt.error && (
                    <span className="ml-2 max-w-[120px] truncate text-xs text-red-500" title={evt.error}>
                      {evt.error}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          {eventsData && eventsData.total > 20 && (
            <p className="mt-2 text-center text-xs text-gray-400">{eventsData.total} events totaal</p>
          )}
        </Card>

        {/* Error Log */}
        <Card>
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Recente Errors & Waarschuwingen</h3>

          <div className="max-h-80 space-y-1.5 overflow-y-auto">
            {errorsLoading ? (
              <div className="animate-pulse space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-10 rounded bg-gray-100" />
                ))}
              </div>
            ) : (errorsData?.logs ?? []).length === 0 ? (
              <div className="flex flex-col items-center py-8">
                <CheckCircle size={24} className="mb-2 text-green-400" />
                <p className="text-sm text-gray-400">Geen recente errors</p>
              </div>
            ) : (
              (errorsData?.logs ?? []).map((log) => (
                <div key={log.id} className="rounded-lg bg-gray-50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', levelColor(log.level))}>
                      {log.level}
                    </span>
                    <span className="text-xs font-medium text-gray-600">{log.source}</span>
                    <span className="text-xs text-gray-400">{formatDatum(log.createdAt)}</span>
                  </div>
                  <p className="mt-1 truncate text-sm text-gray-700">{log.message}</p>
                </div>
              ))
            )}
          </div>

          {errorsData && errorsData.total > 20 && (
            <p className="mt-2 text-center text-xs text-gray-400">{errorsData.total} logs totaal</p>
          )}
        </Card>
      </div>
    </div>
  );
}
